import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { useNavigate } from 'react-router-dom';
import { learningGroupService, LearningGroup } from '../../services/learningGroupService';
import tokenService from '../../services/tokenService';
import GroupCard from './GroupCard';
import { confirmLeaveGroup } from './confirmLeaveGroup';
import CreateGroupDialog from './CreateGroupDialog';

// #413: Content-Manager dürfen Lerngruppen anlegen (Kundenwunsch)
const GROUP_CREATE_ROLES = ['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR'];

const GroupList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [groups, setGroups] = useState<LearningGroup[]>([]);
  const [myGroups, setMyGroups] = useState<LearningGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const canCreateGroup = tokenService.getRoles().some((r) => GROUP_CREATE_ROLES.includes(r));

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const [allGroups, userGroups] = await Promise.all([
        learningGroupService.getGroups(),
        learningGroupService.getMyGroups(),
      ]);
      setGroups(allGroups);
      setMyGroups(userGroups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading groups');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (groupId: number) => {
    setActionLoading(groupId);
    try {
      await learningGroupService.joinGroup(groupId);
      loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error joining group');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveGroup = async (group: LearningGroup) => {
    if (!confirmLeaveGroup(group)) return;
    setActionLoading(group.id);
    try {
      await learningGroupService.leaveGroup(group.id);
      loadGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error leaving group');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewGroup = (groupId: number) => {
    navigate(`/learning-groups/${groupId}`);
  };

  const handleGroupCreated = () => {
    setCreateDialogOpen(false);
    loadGroups();
  };

  const filteredGroups = (tab === 0 ? groups : myGroups).filter((g) =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1f3240' }}>
          {t('learningGroups.title', 'Lerngruppen')}
        </Typography>
        {canCreateGroup && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('learningGroups.create', 'Gruppe erstellen')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('learningGroups.tabs.explore', 'Entdecken')} />
          <Tab label={t('learningGroups.tabs.myGroups', 'Meine Gruppen (%{count})', { count: myGroups.length })} />
        </Tabs>
      </Box>

      <TextField
        fullWidth
        placeholder={t('learningGroups.search', 'Lerngruppe suchen...')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {filteredGroups.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <GroupIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {tab === 0
              ? t('learningGroups.noGroupsAvailable', 'Keine Lerngruppen verfügbar')
              : t('learningGroups.noMyGroups', 'Sie sind noch keiner Lerngruppe beigetreten')}
          </Typography>
          {tab === 1 && (
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setTab(0)}>
              {t('learningGroups.exploreGroups', 'Gruppen entdecken')}
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredGroups.map((group) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={group.id}>
              <GroupCard
                group={group}
                onJoin={() => handleJoinGroup(group.id)}
                onLeave={() => handleLeaveGroup(group)}
                onView={() => handleViewGroup(group.id)}
                loading={actionLoading === group.id}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CreateGroupDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleGroupCreated}
      />
    </Box>
  );
};

export default GroupList;
